import { equals } from '@execonline-inc/collections';
import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { FetchError } from '@execonline-inc/fetch.private';
import { log, warn } from '@execonline-inc/logging';
import { toTask } from '@execonline-inc/maybe-adapter';
import { whenZero } from '@execonline-inc/numbers';
import { assertNever, identity, noop } from '@kofno/piper';
import { Channel } from 'actioncable';
import { fromEmpty } from 'maybeasy';
import { action, autorun, makeObservable, observable, runInAction } from 'mobx';
import { Task } from 'taskarian';
import { v4 as uuid } from 'uuid';
import { currentUserStore } from '../../../../CurrentUserStore';
import fetch from '../../../../Fetch/Init';
import { findLinkByT } from '../../../../Links';
import { Link } from '../../../../Resource/Types';
import { notYetTranslatedText } from '../../../../Translations';
import { createDisposableBehavior } from '../../../../stores/DisposableBehavior';
import { MessagesSuccess, messagesDecoder, streamedChatMessageDecoder } from '../../Decoders';
import { StreamSequencer } from '../../StreamSequencer';
import { AssistantRole, StreamedChatMessage } from '../../Types';
import { cableStore } from '../CableStore';
import { chatMetadataStore } from '../ChatMetadataStore';
import {
  ChatState,
  Message,
  MessagePostError,
  Streaming,
  StreamingAndReceivingInput,
} from './Types';

export class ChatStore {
  @observable state: ChatState = {
    kind: 'disconnected',
    messages: [],
    nextMessage: '',
    retries: 0,
  };

  @observable correlationId?: string;

  @observable private _turnUUIDs: string[] = [];
  private channel?: Channel;
  private previewBehavior = createDisposableBehavior();
  private sequencer = new StreamSequencer<StreamedChatMessage>((turnUUID, currentTurnUUID) =>
    this.isTurnNewer(turnUUID, currentTurnUUID),
  );

  constructor() {
    makeObservable(this);
  }

  get turnUUIDs(): readonly string[] {
    return this._turnUUIDs;
  }

  isTurnNewer = (turnUUID: string, lastTurnUUID: string | null): boolean => {
    if (lastTurnUUID === null) return true;
    const currentIndex = this._turnUUIDs.indexOf(turnUUID);
    const lastIndex = this._turnUUIDs.indexOf(lastTurnUUID);
    const isNewer = currentIndex > lastIndex;

    return isNewer;
  };

  @action
  initialize(): void {
    this.previewBehavior.initialize(() =>
      autorun(() => {
        if (
          currentUserStore.currentUserState.kind === 'identified' &&
          cableStore.state.kind === 'ready'
        ) {
          if (!this.correlationId) {
            this.correlationId = cableStore.state.correlationId;
          }
          this.setupCableSubscription();
        }
      }),
    );
  }

  dispose(): void {
    if (this.channel) {
      this.channel.unsubscribe();
      log('Unsubscribed from ChatChannel');
    }
    this.previewBehavior.dispose();
  }

  @action
  reset(): void {
    if (this.channel) {
      this.channel.unsubscribe();
      log('Unsubscribed from ChatChannel');
    }

    this.state = {
      kind: 'disconnected',
      messages: fromEmpty(this.state.messages)
        .map((messages) => [messages[0]])
        .getOrElseValue([]),
      nextMessage: '',
      retries: 0,
    };
    this._turnUUIDs = [];
    this.sequencer.reset();
    this.channel = undefined;
    this.correlationId = undefined;
  }

  @action
  setInitialMessageBy = (assistantRole: AssistantRole) => {
    switch (assistantRole) {
      case 'coach':
        this.state = {
          ...this.state,
          messages: [
            {
              role: 'assistant',
              content: notYetTranslatedText(
                'Hello! I am EXO, your AI coach advisor, here to assist you in designing a learning collection for any individual or cohort of leaders.\n\nLike a human ExecOnline advisor, I will have a conversation with you to better understand your audience’s leadership development goals, then recommend programs from ExecOnline’s catalog that align to the goals and preview the recommendations in a learning collection that is ready to publish for participants.\n\nOnce the programs are confirmed I can also generate a welcome message for participants based on your specific collection theme(s).\n\n<b>Would you like to get started?</b>',
              ).translationKey,
              turnUUID: uuid(),
            },
          ],
        };
        break;
      case 'default':
        this.state = {
          ...this.state,
          messages: [
            {
              role: 'assistant',
              content: notYetTranslatedText(
                'Hello! I’ll guide you through a series of questions to understand your leadership development goals, and recommend programs from ExecOnline’s catalog that align with those goals.\n\nAs we move along I’ll preview the recommended programs in a learning collection for your leaders, and generate a welcome message based on your specific collection theme(s).\n\nI strive to provide accurate and helpful guidance, but as an AI advisor may occasionally miss the mark. Please let me know if this happens, and I will do my best to get back on track.\n\nYou can save the learning collection at any point to conclude our chat and proceed with onboarding your leaders, or continue refining the collection manually.\n\n<b>Shall we begin?</b>',
              ).translationKey,
              turnUUID: uuid(),
            },
          ],
        };
        break;
    }
  };

  @action
  private addTurnUUID = (uuid: string) => {
    this._turnUUIDs.push(uuid);
  };

  private postMessageToServer = (messagesLink: Link): Task<FetchError, MessagesSuccess> =>
    fetch.post(messagesLink.href, messagesDecoder, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        chat_id: this.correlationId,
        messages: this.state.messages,
        turn_uuid: this.state.messages[this.state.messages.length - 1].turnUUID,
        assistant_role: chatMetadataStore.assistantRole,
        product_license_id: chatMetadataStore.selectedProductLicense
          .map<number | undefined>((license) => license.id)
          .getOrElseValue(undefined),
        course_registration_id: chatMetadataStore.courseRegistrationId
          .map<number | undefined>(identity)
          .getOrElseValue(undefined),
      }),
    });

  @action
  private setMessageErrorState = (error: MessagePostError) => {
    warn('Failed to send message', error.kind);
    this.state = {
      ...this.state,
      kind: 'error',
      message: `Failed to send message: ${error.kind}`,
      retries: this.state.retries,
    };
  };

  @action
  private transitionStreamToReady = (stream: string) => {
    switch (this.state.kind) {
      case 'streaming':
      case 'streaming-and-receiving-input':
        const newMessages: Message[] = [
          ...this.state.messages,
          {
            role: 'assistant',
            content: stream,
            turnUUID: this.state.messages[this.state.messages.length - 1].turnUUID,
          },
        ];
        this.state = {
          ...this.state,
          kind: 'ready',
          messages: newMessages,
        };
        break;
      case 'ready':
      case 'error':
      case 'disconnected':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateNextMessage = (message: string) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = {
          ...this.state,
          kind: 'ready',
          nextMessage: message,
        };
        break;
      case 'streaming-and-receiving-input':
      case 'streaming':
        this.state = {
          ...this.state,
          kind: 'streaming-and-receiving-input',
          nextMessage: message,
        };
        break;
      case 'error':
      case 'disconnected':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  private postMessagesTask = () => {
    Task.succeed<MessagePostError, {}>({})
      .andThen(() => findLinkByT({ rel: 'messages' }, currentUserStore.links))
      .andThen(this.postMessageToServer)
      .fork((e) => this.setMessageErrorState(e), noop);
  };

  @action
  sendMessage = () => {
    if (this.state.kind !== 'ready') return;
    if (this.state.nextMessage.trim() === '') return;

    const newTurnUUID = uuid();
    this.addTurnUUID(newTurnUUID);

    this.state = {
      kind: 'streaming',
      messages: [
        ...this.state.messages,
        { role: 'user', content: this.state.nextMessage, turnUUID: newTurnUUID },
      ],
      nextMessage: '',
      retries: 0,
      stream: '',
    };
    this.postMessagesTask();
  };

  @action
  private setupNewTurnUUID = (state: ChatState): ChatState => {
    if (state.kind === 'disconnected') return state;

    const messages = state.messages;

    if (messages.length > 0) {
      const newTurnUUID = uuid();
      this.addTurnUUID(newTurnUUID);
      messages[messages.length - 1] = {
        ...messages[messages.length - 1],
        turnUUID: newTurnUUID,
      };
    }

    return {
      kind: 'streaming',
      messages,
      nextMessage: '',
      retries: state.retries + 1,
      stream: '',
    };
  };

  @action
  retryLastMessage = () => {
    if (this.state.kind === 'disconnected') return;
    this.state = this.setupNewTurnUUID(this.state);
    this.postMessagesTask();
  };

  @action
  handleStreamError = () => {
    if (this.state.kind === 'error' || this.state.kind === 'disconnected') return;

    toTask({}, whenZero(this.state.retries)).fork(
      (_) =>
        (this.state = {
          ...this.state,
          kind: 'error',
          message: 'Stream error occurred',
          retries: this.state.retries,
        }),
      (_) => {
        this.state = this.setupNewTurnUUID(this.state);
        this.postMessagesTask();
      },
    );
  };

  @action
  private handleStreamMessage = (
    state: Streaming | StreamingAndReceivingInput,
    data: StreamedChatMessage,
  ) => {
    switch (data.type) {
      case 'chunk':
        this.state = {
          ...state,
          stream: state.stream + data.content,
        };
        break;
      case 'full-text':
        this.state = {
          ...state,
          stream: data.content,
        };
        break;
      case 'end-of-stream':
        this.transitionStreamToReady(state.stream);
        break;
      case 'stream-error':
        this.handleStreamError();
        break;
    }
  };

  @action
  private handleChatMessage = (data: StreamedChatMessage) => {
    switch (this.state.kind) {
      case 'streaming':
      case 'streaming-and-receiving-input':
        this.handleStreamMessage(this.state, data);
        break;
      case 'ready':
        if (data.type === 'stream-error') {
          this.handleStreamError();
        }
        break;
      case 'error':
      case 'disconnected':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  private handleErrorReceiveData = (error: string) => {
    warnAndNotify('StreamedMessageParseError', 'Failed to parse chat stream message.', {
      error,
    });

    this.state = {
      kind: 'error',
      messages: this.state.messages,
      nextMessage: this.state.nextMessage,
      message: 'Failed to parse chat stream message',
      retries: this.state.retries,
    };
  };

  @action
  private handleReceiveData = (data: string) => {
    streamedChatMessageDecoder.decodeJson(data).cata({
      Ok: (data) => {
        const lastMessage = this.state.messages[this.state.messages.length - 1];

        if (
          equals(data.turnUUID, lastMessage.turnUUID) ||
          this.isTurnNewer(data.turnUUID, lastMessage.turnUUID)
        ) {
          this.handleSequencedMessage(data);
        } else {
          log('Skipping message - older turn', {
            turnUUID: data.turnUUID,
            lastTurnUUID: lastMessage.turnUUID,
          });
        }
      },
      Err: this.handleErrorReceiveData,
    });
  };

  /**
   * A stream error ends the turn, so it is applied at once. Every other message
   * goes through the sequencer, which returns it (and any messages it unblocks)
   * in the order the server sent them.
   */
  @action
  private handleSequencedMessage = (data: StreamedChatMessage) => {
    if (data.type === 'stream-error') {
      this.handleChatMessage(data);
      return;
    }

    this.sequencer.accept(data.turnUUID, data.seq, data).forEach(this.handleChatMessage);
  };

  @action
  private setupCableSubscription = () => {
    if (cableStore.state.kind !== 'ready' || !this.correlationId) return;

    this.channel = cableStore.state.cable.subscriptions.create(
      { channel: 'EnterprisePortal::ChatChannel', chat_id: this.correlationId },
      {
        connected: () => {
          log('Connected to ChatChannel');
          if (this.state.kind === 'disconnected') {
            runInAction(() => {
              this.state = {
                ...this.state,
                kind: 'ready',
                retries: 0,
              };
            });
          }
        },
        disconnected: () => {
          log('Disconnected from ChatChannel');
          if (this.state.kind !== 'error' && this.state.kind !== 'disconnected') {
            runInAction(() => {
              this.state = {
                kind: 'disconnected',
                messages: this.state.messages,
                nextMessage: this.state.nextMessage,
                retries: this.state.retries,
              };
            });
          }
        },
        received: this.handleReceiveData,
      },
    );

    this.state = {
      ...this.state,
      kind: 'ready',
      retries: 0,
    };
  };
}

const chatStore = new ChatStore();
chatStore.initialize();

export { chatStore };
